import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OLAOutput } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext } from '@amzn/awscat-react-components';
import { Alert, Box, ColumnLayout, Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { defaultSortBy } from '../../../common/defaultTableSortState';
import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumber } from '../../../common/LocalizationHelper';
import AWSCATTable from '../../../common/table/Table-index';
import { MatchParam } from '../../../common/types';
import { isAWSUser } from '../../../common/util';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../contexts/TcoContext';
import PreviewBanner from '../../common/PreviewBanner';
import { OLASummaryItem, sqlServerTableCols, sqlServerTableHiddenColumns, summaryTableCols } from './SqlServerOptimizationHelper';

type Props = FlashContextInterface & AuthContextInterface;

const tcoService = new TcoService();
const ec2ByolCalcUrl = 'https://www.ec2byolcalc.com/';
const gbyteUrl = 'https://gbyte.co/blog/finding-databases-use-Enterprise-features-sql-server';
const microsoftUrl = 'https://docs.microsoft.com/en-us/sql/relational-databases/'
    +'system-dynamic-management-views/sys-dm-db-persisted-sku-features-transact-sql?view=sql-server-2017';

function SqlServerOptimization({ flash, auth }: Props ) {
    const isAWS = isAWSUser(auth);
    const isMounted = useIsMounted();
    const [ loading, setLoading ] = useState(false);
    const { assessment, tcoLoading } = useContext(TcoContext);
    const [ olaOutput, setOlaOutput ] = useState<OLAOutput | null>(null);
    const [ currency, setCurrency ] = useState('');
    const [ activeTabId, setActiveTabId ] = useState('sqlServerOptimization');
    const [ showNoSavingsInfo, setShowNoSavingsInfo ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const loadServerOptimizationData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const olaAssessment = await tcoService.getAwsOptimizationAndLicensing(tcoAssessmentId);
                if (isMounted()) {
                    setOlaOutput(olaAssessment.output.ola);
                    setCurrency(olaAssessment.output.currency);
                    if (olaAssessment.output.ola.sqlServer.total.costDifference < 0) {
                        setShowNoSavingsInfo(true);
                    }
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_OLA);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.SQL_SERVER_OPTIMIZATION);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadServerOptimizationData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildSqlServerContent = (olaOutput: OLAOutput): JSX.Element => {
        const summaryItems: OLASummaryItem[] = [
            { edition: constants.OLA_SUMMARY_TABLE_EDITION_ENTERPRISE, ...olaOutput.sqlServer.enterprise },
            { edition: constants.OLA_SUMMARY_TABLE_EDITION_STANDARD, ...olaOutput.sqlServer.standard },
            { edition: constants.OLA_SUMMARY_TABLE_EDITION_DEVELOPER, ...olaOutput.sqlServer.developer },
        ];

        return (
            <SpaceBetween size='l'>
                { showNoSavingsInfo ? (
                    <Alert
                        data-testid='noSavingsAlert'
                        dismissible
                        onDismiss={() => setShowNoSavingsInfo(false)}
                    >
                        {constants.OLA_SQL_NO_COST_DIFF}
                    </Alert>
                ) : null }
                <Table
                    columnDefinitions={summaryTableCols(currency)}
                    items={summaryItems}
                    sortingDisabled
                />
                <ColumnLayout disableGutters columns={2} variant='default' className='totals-grid'>
                    <Box variant='h3'>{constants.TOTAL_REDUCTION}</Box>
                    <Box variant='h3' float='right'>
                        {formatNumber(olaOutput.sqlServer.total.countDifferenceWithConsolidation, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                    </Box>
                    <Box variant='h3'><strong>{constants.TOTAL_SAVINGS_HEADER}</strong></Box>
                    <Box variant='h3' float='right'><strong>
                        {formatCurrencyNoDecimals(
                            olaOutput.sqlServer.total.costDifferenceWithConsolidation, currency,
                        )}
                    </strong></Box>
                </ColumnLayout>
                <AWSCATTable
                    resourceName={constants.SERVER_TABLE_HEADER}
                    columnDefinitions={sqlServerTableCols(currency)}
                    items={olaOutput.sqlServer.servers || []}
                    enableAutoSorting
                    defaultSortBy={defaultSortBy('serverId', false)}
                    useDefaultDownload
                    resizeable
                    autoGenerateVisibleContent
                    defaultHiddenColumns={sqlServerTableHiddenColumns}
                    downloadFileName={constants.OLA_TAB_SQL_SERVER_FILENAME}
                />
            </SpaceBetween>
        );
    };

    const sqlEditionContent: JSX.Element = (
        <Alert>
            {constants.OLA_SQL_EDITION_DETAILS}
            <br />
            {constants.OLA_SQL_EDITION_INSTRUCTION}
            <ol>
                <li>
                    {constants.OLA_SQL_EDITION_STEP_ONE_RUN}
                    <a href={gbyteUrl} target='_blank' rel='noopener noreferrer'>
                        {constants.OLA_SQL_EDITION_STEP_ONE_THIS_SCRIPT}
                    </a>
                    {constants.OLA_SQL_EDITION_STEP_ONE_OR}
                    <a href={microsoftUrl} target='_blank' rel='noopener noreferrer'>
                        {constants.OLA_SQL_EDITION_STEP_ONE_HERE}
                    </a>
                </li>
                <li>{constants.OLA_SQL_EDITION_STEP_TWO}</li>
                <li>{constants.OLA_SQL_EDITION_STEP_THREE}</li>
                <li>{constants.OLA_SQL_EDITION_STEP_FOUR}</li>
            </ol>
        </Alert>
    );

    const buildOLAPageContent = (): JSX.Element => {
        if (tcoLoading || loading || !olaOutput) {
            return (
                <Spinner size='big'/>
            );
        }

        const titleDescription = isAWS ? (
            <span>{constants.OLA_TITLE_DESCRIPTION}
                <a href={ec2ByolCalcUrl} target='_blank' rel='noopener noreferrer'>
                    {constants.OLA_TITLE_DESCRIPTION_SOURCE}
                </a></span>
        ) : null;

        const tabs: TabsProps.Tab[] = [{
            id: 'sqlServerOptimization',
            label: constants.OLA_TAB_SQL_SERVER_LABEL,
            content: buildSqlServerContent(olaOutput),
        }, {
            id: 'sqlEditionOptimization',
            label: constants.OLA_TAB_SQL_EDITION_LABEL,
            content: sqlEditionContent,
        }];

        return (
            <SpaceBetween size='l'>
                <PreviewBanner />
                <Header variant='h1' description={titleDescription}>{constants.OLA_TITLE}</Header>
                <Tabs tabs={tabs} activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} />
            </SpaceBetween>
        );
    };

    return !tcoLoading ? (
        <Container>
            {buildOLAPageContent()}
        </Container>
    )
        : null;
}
export default withAuthContext(SqlServerOptimization);
